import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  TextField as MuiTextField,
  CircularProgress as MuiCircularProgress,
  Autocomplete as MuiAutocomplete,
} from "@mui/material";
import {
  Search as MuiSearchIcon,
  ArrowDropDown as MuiArrowDropDownIcon,
} from "@mui/icons-material";
import axios from "axios";
import PropTypes from "prop-types";
import * as qs from "qs";
import { useAlerts } from "common";

export const AUTOCOMPLETE_DEBOUNCE_DELAY = 500; // Delay after user pauses typing before calling API (in milliseconds)
export const AUTOCOMPLETE_MIN_INPUT = 2; // Minimum number of characters entered by user before calling API

/**
 * `Autocomplete` is a component that renders an MUI autocomplete input for filtering.
 *
 * @component
 * @param {string} label - The label for the autocomplete input.
 * @param {string} value - The value of the autocomplete.
 * @param {Function} onChange - The function to call when the autocomplete input's value changes.
 * @param {boolean} [required=false] - Indicates if the autocomplete input is a required field.
 * @param {boolean} [disabled=false] - Indicates if the autocomplete input is disabled.
 * @param {boolean} [clientSide=false] - Indicates if the autocomplete is a client side.
 * @param {Array} [options=[]] - The initial set of options displayed in the autocomplete dropdown/popup.
 */

let timeoutID;
export const Autocomplete = ({
  label,
  value,
  options = [],
  onChange,
  autoCompleteUrlPrefix,
  required = false,
  disabled = false,
  clientSide = false,
  noOptionsText = "",
  size = "small",
  clientSideLoading = false,
  ...passedParams
}) => {
  const { setAlert, clearAlert } = useAlerts();
  const { t } = useTranslation();

  const [optionsList, setOptionsList] = useState(options || []);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (options.length) setOptionsList(options);
  }, [options]);

  const handleSearchStringChange = (event) => {
    const searchQuery = event.target.value.trim();

    clearTimeout(timeoutID); // Cancel current debounce timer

    // Start debounce timer and call API when timer finishes
    timeoutID = setTimeout(async () => {
      if (searchQuery?.length >= AUTOCOMPLETE_MIN_INPUT) {
        const cancelSource = axios.CancelToken.source();
        try {
          setLoading(true);
          clearAlert();
          const fetchSize = 10;

          const request = {
            url: autoCompleteUrlPrefix,
            params: { value: searchQuery, size: fetchSize },
            // paramsSerializer with qs helps us to encode the special characters that are not encoded by the Axios.
            paramsSerializer: {
              serialize: (params) => {
                return qs.stringify(params, { arrayFormat: "repeat" });
              },
            },
            cancelToken: cancelSource?.token,
          };

          const response = await axios(request);

          const options = response?.data?.values || [];
          // pushing helper text as an option when the options are greater than or equal to fetchSize
          if (options.length >= fetchSize) {
            options.push(moreItemsOption(options.length, t));
          }
          setOptionsList(options);
        } catch (error) {
          setAlert("error", error?.message);
        } finally {
          setLoading(false);
        }
      } else {
        setOptionsList([searchTooShortOption(AUTOCOMPLETE_MIN_INPUT, t)]);
      }
    }, AUTOCOMPLETE_DEBOUNCE_DELAY);
  };

  return (
    <MuiAutocomplete
      {...passedParams}
      options={optionsList}
      getOptionDisabled={(option) => option.isDummy}
      defaultValue={null}
      value={value}
      autoComplete
      clearOnEscape
      clearOnBlur
      loading={clientSideLoading ? clientSideLoading : loading}
      onChange={onChange}
      disabled={disabled}
      onClose={() => !clientSide && setOptionsList([])}
      noOptionsText={
        noOptionsText ? noOptionsText : t("globals.autocomplete.helperText")
      }
      popupIcon={
        clientSide ? (
          <MuiArrowDropDownIcon fontSize="small" />
        ) : (
          <MuiSearchIcon fontSize="small" />
        )
      }
      sx={{
        "& .MuiAutocomplete-popupIndicatorOpen": {
          // Stop MUI from rotating the icon by rotating it back
          rotate: !clientSide && "180deg",
        },
        "& .MuiAutocomplete-endAdornment .MuiIconButton-root": {
          // Move dropdown arrow and clear X up to horizontal center
          height: "auto",
        },
        "& .MuiInputLabel-root, .MuiInputBase-input": {
          fontSize: size === "small" ? "14.5px" : "",
        },
      }}
      renderInput={(params) => (
        <RenderInput
          params={params}
          clientSide={clientSide}
          label={label}
          required={required}
          loading={loading}
          handleSearchStringChange={handleSearchStringChange}
          size={size}
        />
      )}
    />
  );
};

Autocomplete.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.any,
  onChange: PropTypes.func,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  clientSide: PropTypes.bool,
  options: PropTypes.array,
  autoCompleteUrlPrefix: PropTypes.string,
  noOptionsText: PropTypes.string,
  size: PropTypes.string,
  clientSideLoading: PropTypes.bool,
};

const searchTooShortOption = (nMinChars, t) => ({
  value: {},
  displayText: t("globals.autocomplete.searchTooShortText", {
    nMinChars: nMinChars,
  }),
  isDummy: true,
});

const moreItemsOption = (nResults, t) => ({
  value: {},
  displayText: t("globals.autocomplete.moreItemsText", {
    nResults: nResults,
  }),
  isDummy: true,
});

const RenderInput = ({
  params,
  clientSide,
  label,
  required,
  loading,
  handleSearchStringChange,
  size,
}) => {
  if (clientSide) {
    return (
      <MuiTextField
        {...params}
        label={label}
        variant="outlined"
        size={size}
        required={required}
        sx={{
          fontWeight: 500,
          lineHeight: "22.5px",
          width: "100%",
        }}
        slotProps={{
          ...params.slotProps,
          input: {
            ...params.slotProps.input,
            endAdornment: params.slotProps.input.endAdornment,
          },
        }}
      />
    );
  }

  return (
    <MuiTextField
      {...params}
      label={label}
      variant="outlined"
      size={size}
      sx={{
        fontWeight: 500,
        lineHeight: "22.5px",
        width: "100%",
      }}
      required={required}
      onChange={handleSearchStringChange}
      slotProps={{
        ...params.slotProps,
        input: {
          ...params.slotProps.input,
          endAdornment: (
            <>
              {loading ? (
                <MuiCircularProgress color="inherit" size={20} />
              ) : null}
              {params.slotProps.input.endAdornment}
            </>
          ),
        },
      }}
    />
  );
};
